export type ThemeChoice =
  | 'system'
  | 'light'
  | 'dark'
  | 'platinum'
  | 'obsidian'
  | 'ocean'
  | 'forest'
  | 'rose'
  | string

export const SUPPORTED_THEMES: ThemeChoice[] = [
  'system',
  'light',
  'dark',
  'platinum',
  'obsidian',
  'ocean',
  'forest',
  'rose',
]

const STORAGE_KEY = 'theme'

let mediaQuery: MediaQueryList | null = null
let mediaListener: ((e: MediaQueryListEvent) => void) | null = null

export function getSavedTheme(): ThemeChoice | null {
  try {
    return (localStorage.getItem(STORAGE_KEY) as ThemeChoice) || null
  } catch {
    return null
  }
}

export function saveTheme(theme: ThemeChoice) {
  try {
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    /* noop */
  }
}

export function clearSavedTheme() {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* noop */
  }
}

export function systemActiveTheme(): 'light' | 'dark' {
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}

function cleanupSystemListener() {
  if (mediaQuery && mediaListener) {
    try {
      mediaQuery.removeEventListener('change', mediaListener)
    } catch {
      // Safari < 14 fallback
      // @ts-expect-error - Safari < 14 fallback
      mediaQuery.removeListener?.(mediaListener)
    }
  }
  mediaQuery = null
  mediaListener = null
}

function setupSystemListener() {
  cleanupSystemListener()
  mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  mediaListener = () => {
    const next = systemActiveTheme()
    document.documentElement.setAttribute('data-theme', next)
  }
  try {
    mediaQuery.addEventListener('change', mediaListener)
  } catch {
    // Safari < 14 fallback
    // @ts-expect-error - Safari < 14 fallback
    mediaQuery.addListener?.(mediaListener)
  }
}

export function applyTheme(theme: ThemeChoice) {
  if (theme === 'system') {
    // Persist the preference
    saveTheme('system')
    // Mirror system preference to a concrete theme and keep it in sync
    const active = systemActiveTheme()
    document.documentElement.setAttribute('data-theme', active)
    setupSystemListener()
    return
  }

  // Apply explicit theme and remove system listener
  cleanupSystemListener()
  document.documentElement.setAttribute('data-theme', theme)
  saveTheme(theme)
}

export function initTheme(defaultToSystem = true): ThemeChoice {
  const saved = getSavedTheme()
  if (saved) {
    applyTheme(saved)
    return saved
  }
  const fallback: ThemeChoice = defaultToSystem ? 'system' : 'light'
  applyTheme(fallback)
  return fallback
}
